import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import TenantGuard from './TenantGuard';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperadmin?: boolean;
}

// Mapeo de rutas a permisos
const routePermissionMap: Record<string, string> = {
  '/pos': 'pos',
  '/ventas': 'ventas',
  '/inventario': 'inventario',
  '/clientes': 'clientes',
  '/ingresos': 'ingresos',
  '/cartera': 'cartera',
  '/reportes': 'reportes',
  '/usuarios': 'usuarios',
  '/configuracion': 'configuracion',
};

export default function ProtectedRoute({ children, requireSuperadmin = false }: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <i className="ri-store-3-line text-3xl text-white"></i>
          </div>
          <p className="text-sm text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    return <Navigate to="/login" replace />;
  }

  if (profile.status !== 'Activo') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl text-red-500"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Cuenta inactiva</h2>
          <p className="text-sm text-gray-600 mb-6">
            Tu cuenta ha sido desactivada. Contacta al administrador para más información.
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-teal-500 text-white px-6 py-2 rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium whitespace-nowrap"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // Si la ruta requiere ser Superadmin
  if (requireSuperadmin) {
    if (profile.role !== 'Superadmin') {
      return <Navigate to="/" replace />;
    }
  } else if (profile.role !== 'Administrador' && profile.role !== 'Superadmin') {
    // Verificar permisos de módulo (Admin y Superadmin siempre tienen acceso)
    const requiredPermission = routePermissionMap[location.pathname];
    if (requiredPermission && profile.permissions) {
      const hasPermission = profile.permissions[requiredPermission] === true;
      if (!hasPermission) {
        return <Navigate to="/" replace />;
      }
    }
  }

  return <TenantGuard>{children}</TenantGuard>;
}